import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureGiantLevelAssets } from '../../assets/preloadSessionLevelAssets';
import {
  beginLevelAttempt,
  updateActiveLevelSnapshot,
} from '../../store/levelRunSession';
import type { LevelRunSnapshot } from '../../store/settleLevelRun';
import {
  createPlayfieldMotionBindings,
  type PlayfieldMotionBindings,
} from './gameplay/playfieldMotionBindings';
import {
  createLevelScreenModel,
  type LevelScreenProps,
} from './LevelScreenModelScreen';
import { LevelScreenController } from './LevelScreenController';
import { LevelScreenView } from './LevelScreenView';

export type { LevelScreenProps } from './LevelScreenModelScreen';

export function LevelScreen(props: LevelScreenProps) {
  const giantId = props.giantId ?? 'mona';
  const orderIndex = props.orderIndex ?? 0;
  const levelKey = props.levelKey ?? 0;
  const [levelAssetsReady, setLevelAssetsReady] = useState(false);
  const [minigameOpen, setMinigameOpen] = useState(false);
  const [secondChanceRestoreKey, setSecondChanceRestoreKey] = useState(0);
  const [pendingFailSnapshot, setPendingFailSnapshot] =
    useState<LevelRunSnapshot | null>(null);

  useEffect(() => {
    beginLevelAttempt(giantId, orderIndex, levelKey);
    setMinigameOpen(false);
    setSecondChanceRestoreKey(0);
    setPendingFailSnapshot(null);
  }, [giantId, orderIndex, levelKey]);

  useEffect(() => {
    let cancelled = false;
    setLevelAssetsReady(false);
    void ensureGiantLevelAssets(giantId).then(() => {
      if (!cancelled) {
        setLevelAssetsReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [giantId, levelKey]);

  const motionBindingsRef = useRef<PlayfieldMotionBindings>(createPlayfieldMotionBindings());
  const [hud, setHud] = useState({
    health: props.health ?? 5,
    maxHealth: props.maxHealth ?? 5,
    collectiblesCollected: props.collectiblesCollected ?? 0,
    collectiblesTotal: props.collectiblesTotal ?? 0,
    progress: props.progress ?? 0,
  });

  const onHudUpdate = useCallback(
    (next: {
      health: number;
      maxHealth: number;
      collectiblesCollected: number;
      collectiblesTotal: number;
    }) => {
      setHud((prev) => ({
        ...prev,
        health: next.health,
        maxHealth: next.maxHealth,
        collectiblesCollected: next.collectiblesCollected,
        collectiblesTotal: next.collectiblesTotal,
      }));
    },
    [],
  );

  const onRunSnapshotUpdate = useCallback((snapshot: LevelRunSnapshot) => {
    updateActiveLevelSnapshot(snapshot);
  }, []);

  const onSecondChanceRequest = useCallback((snapshot: LevelRunSnapshot) => {
    setPendingFailSnapshot(snapshot);
    setMinigameOpen(true);
  }, []);

  const onMinigameWin = useCallback(() => {
    setMinigameOpen(false);
    setPendingFailSnapshot(null);
    setSecondChanceRestoreKey((key) => key + 1);
  }, []);

  const onFail = props.onFail;
  const onMinigameLose = useCallback(
    (snapshot: LevelRunSnapshot) => {
      setMinigameOpen(false);
      onFail?.(snapshot);
    },
    [onFail],
  );

  const model = createLevelScreenModel({
    ...props,
    ...hud,
  });
  const controller = new LevelScreenController(model);

  if (!levelAssetsReady) {
    return null;
  }

  return (
    <LevelScreenView
      model={model}
      controller={controller}
      characterId={props.characterId ?? 'eddy'}
      isPaused={props.isPaused ?? false}
      minigameOpen={minigameOpen}
      secondChanceRestoreKey={secondChanceRestoreKey}
      motionBindingsRef={motionBindingsRef}
      onHudUpdate={onHudUpdate}
      onRunSnapshotUpdate={onRunSnapshotUpdate}
      onSecondChanceRequest={onSecondChanceRequest}
      onMinigameWin={onMinigameWin}
      onMinigameLose={onMinigameLose}
      pendingFailSnapshot={pendingFailSnapshot}
    />
  );
}
